/**
 * The split frame every sign-in page sits in: the wordmark and the form on the surface, a
 * dark band beside it on wide screens, and the theme switch (plus anything else the page
 * puts in `corner`) pinned to the top right.
 */
import { APP_NAME } from '@layerit/brand'
import type { ReactNode } from 'react'
import { BrandMark } from './brand-mark'
import { ThemeMenu } from './theme-menu'

export function AuthFrame({
	corner,
	wordmarkHref,
	product,
	aside,
	children,
}: {
	corner?: ReactNode
	wordmarkHref?: string | null
	/** A second word after the wordmark, e.g. "Admin". */
	product?: string
	aside: ReactNode
	children: ReactNode
}) {
	const wordmark = (
		<>
			<BrandMark size={28} />
			<span className="text-sm font-semibold tracking-tight text-ink">{APP_NAME}</span>
			{product !== undefined && (
				<span className="rounded-md border border-line-strong px-1.5 py-0.5 text-xs font-medium text-ink-2">{product}</span>
			)}
		</>
	)

	return (
		<div className="relative grid min-h-screen grid-cols-1 lg:grid-cols-2">
			<div className="absolute top-8 right-6 col-span-full flex items-center gap-2 lg:top-10 lg:right-14">
				<ThemeMenu className="lg:text-white/80 lg:hover:bg-white/10 lg:hover:text-white lg:aria-expanded:bg-white/10 lg:aria-expanded:text-white" />
				{corner}
			</div>

			<section className="flex flex-col bg-surface px-6 py-8 lg:px-14 lg:py-10">
				<div className="flex items-center">
					{wordmarkHref ? (
						<a className="flex w-fit items-center gap-2.5 rounded-md transition-opacity hover:opacity-80" href={wordmarkHref}>
							{wordmark}
						</a>
					) : (
						<div className="flex w-fit items-center gap-2.5">{wordmark}</div>
					)}
				</div>

				<div className="mx-auto flex w-full max-w-100 flex-1 flex-col justify-center py-10">{children}</div>
			</section>

			<section className="band-ink hidden flex-col px-6 py-14 lg:flex lg:justify-center lg:px-14 lg:py-10">
				<div className="flex flex-1 items-center">
					<div data-reveal className="flex max-w-lg flex-col gap-5">
						{aside}
					</div>
				</div>
			</section>
		</div>
	)
}
